import { useEffect, useMemo, useState } from "react";
import { analyticsAPI } from "../api/client";
import "./AnalyticsDashboard.css";
import { CardSkeleton } from "../components/LoadingSkeleton";

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function number(value) {
  return Number(value || 0);
}

function formatNumber(value, digits = 0) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(number(value));
}

function formatCost(value, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(number(value));
  } catch {
    return `${currency || "USD"} ${formatNumber(value, 2)}`;
  }
}

function EmptyState({ children }) {
  return <div className="analytics-empty">{children}</div>;
}

function Table({ columns, rows, empty }) {
  if (!rows?.length) return <EmptyState>{empty}</EmptyState>;
  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table">
        <thead><tr>{columns.map((column) => <th scope="col" key={column.key}>{column.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || `${index}-${columns[0]?.key}`}>
              {columns.map((column) => <td key={column.key}>{column.render ? column.render(row) : row[column.key] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const today = useMemo(() => new Date(), []);
  const initialFrom = useMemo(() => new Date(today.getTime() - 29 * 86400000), [today]);
  const [from, setFrom] = useState(isoDate(initialFrom));
  const [to, setTo] = useState(isoDate(today));
  const [businessUnit, setBusinessUnit] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    analyticsAPI.getSummary({
      from: `${from}T00:00:00.000Z`,
      to: `${to}T23:59:59.999Z`,
      ...(businessUnit ? { businessUnit } : {}),
    }).then((response) => {
      if (!cancelled) setData(response.data);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.message || "Unable to load analytics data.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [from, to, businessUnit]);

  const people = data?.people || {};
  const totalCost = (data?.awsCosts || []).reduce((sum, row) => sum + number(row.allocated_cost), 0);
  const costCurrency = data?.awsCosts?.[0]?.currency || "USD";
  const totalRequests = (data?.apiHealth || []).reduce((sum, row) => sum + number(row.request_count), 0);
  const totalErrors = (data?.apiHealth || []).reduce((sum, row) => sum + number(row.error_count), 0);
  // 304 responses are cache revalidations, not served work. Averaging them in
  // made a healthy API look 98% broken and buried the endpoints doing real work.
  const cacheRevalidations = (data?.apiHealth || []).reduce((sum, row) => sum + number(row.cache_revalidation_count), 0);
  const servedRequests = (data?.apiHealth || []).reduce(
    (sum, row) => sum + (row.served_count === undefined || row.served_count === null ? number(row.request_count) : number(row.served_count)),
    0
  );
  const errorRate = servedRequests ? (totalErrors / servedRequests) * 100 : 0;
  // Page views show where people went; actions show what they actually did.
  const totalActions = (data?.featureActions || []).reduce((sum, row) => sum + number(row.action_count), 0);

  // Portal adoption, stated so employees and login accounts are never conflated:
  // the headcount and the account list are different populations.
  const activeEmployees = number(people.active_employee_count);
  const employeesWithPortal = number(people.employee_with_portal_count);
  const employeesWithoutPortal = number(people.employee_without_portal_count);
  const portalUsers = number(people.portal_user_count);
  const serviceAccounts = number(people.service_account_count);
  const adoptionRate = activeEmployees ? (employeesWithPortal / activeEmployees) * 100 : 0;

  return (
    <section className="analytics-page">
      <div className="analytics-heading">
        <div>
          <p className="analytics-eyebrow">ADMIN ANALYTICS</p>
          <h1>Workforce, adoption and cloud cost</h1>
          <p>Employee headcount, portal accounts, API health and AWS infrastructure in one view. Service accounts are excluded from adoption figures.</p>
        </div>
        <div className="analytics-filters">
          <label>From<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
          <label>To<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
          <label>Business unit
            <select value={businessUnit} onChange={(event) => setBusinessUnit(event.target.value)}>
              <option value="">All business units</option>
              {(data?.businessUnits || []).map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </select>
          </label>
        </div>
      </div>

      {error ? <div className="analytics-error">{error}</div> : null}
      {loading && !data ? (
        <div style={{ padding: 24 }}>
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : null}

      {data ? <>
        <div className="analytics-kpis">
          <article><span>Active employees</span><strong>{formatNumber(activeEmployees)}</strong><small>{businessUnit || "All business units"}</small></article>
          <article><span>Active HR team</span><strong>{formatNumber(people.hr_count)}</strong><small>Role and department classified</small></article>
          <article><span>Employees with portal login</span><strong>{formatNumber(employeesWithPortal)}</strong><small>{formatNumber(adoptionRate, 1)}% of {formatNumber(activeEmployees)} employees</small></article>
          <article><span>Employees without login</span><strong>{formatNumber(employeesWithoutPortal)}</strong><small>No portal account yet</small></article>
          <article><span>Portal user accounts</span><strong>{formatNumber(portalUsers)}</strong><small>{number(people.active_portal_user_count)} seen in the last 30 days</small></article>
          <article><span>Service accounts</span><strong>{formatNumber(serviceAccounts)}</strong><small>Bot logins, excluded from adoption</small></article>
          <article><span>Tracked API calls</span><strong>{formatNumber(totalRequests)}</strong><small>{formatNumber(cacheRevalidations)} cache revalidations (304)</small></article>
          <article><span>API error rate</span><strong>{formatNumber(errorRate, 1)}%</strong><small>{formatNumber(totalErrors)} failed of {formatNumber(servedRequests)} served</small></article>
          <article><span>Recorded actions</span><strong>{formatNumber(totalActions)}</strong><small>Creates, edits and deletions</small></article>
          <article className="cost"><span>Allocated AWS cost</span><strong>{formatCost(totalCost, costCurrency)}</strong><small>Selected reporting period</small></article>
        </div>

        <div className="analytics-grid">
          <article className="analytics-panel wide">
            <div className="panel-title"><div><h2>Feature and tab usage</h2><p>Most-used portal areas by real users; bot traffic excluded</p></div></div>
            <Table
              rows={data.featureUsage}
              empty="Usage will appear as employees navigate the portal."
              columns={[
                { key: "feature", label: "Feature / tab" },
                { key: "event_count", label: "Events", render: (row) => formatNumber(row.event_count) },
                { key: "unique_users", label: "Unique users", render: (row) => formatNumber(row.unique_users) },
                { key: "last_used_at", label: "Last used", render: (row) => row.last_used_at ? new Date(row.last_used_at).toLocaleString() : "—" },
              ]}
            />
          </article>

          <article className="analytics-panel wide">
            <div className="panel-title"><div><h2>Actions performed</h2><p>What users actually did, not just pages they opened</p></div></div>
            <Table
              rows={data.featureActions}
              empty="No recorded actions yet. Actions are captured from create, edit and delete operations in the portal."
              columns={[
                { key: "action_name", label: "Action" },
                { key: "entity_type", label: "Record type", render: (row) => row.entity_type || "—" },
                { key: "action_count", label: "Times", render: (row) => formatNumber(row.action_count) },
                { key: "unique_users", label: "Users", render: (row) => formatNumber(row.unique_users) },
                { key: "failed_count", label: "Failed", render: (row) => formatNumber(row.failed_count) },
                { key: "last_used_at", label: "Last used", render: (row) => row.last_used_at ? new Date(row.last_used_at).toLocaleString() : "—" },
              ]}
            />
          </article>

          <article className="analytics-panel">
            <div className="panel-title"><div><h2>AWS service cost</h2><p>Allocated spend by service</p></div></div>
            <Table
              rows={data.awsCosts}
              empty="No AWS billing records yet. Connect the CUR exporter to populate service costs."
              columns={[
                { key: "service_name", label: "Service" },
                { key: "allocated_cost", label: "Cost", render: (row) => formatCost(row.allocated_cost, row.currency) },
              ]}
            />
          </article>

          <article className="analytics-panel">
            <div className="panel-title"><div><h2>Server and resource cost</h2><p>Highest-cost infrastructure resources</p></div></div>
            <Table
              rows={data.serverCosts}
              empty="No resource-level costs yet. Enable resource IDs in AWS CUR."
              columns={[
                { key: "resource_name", label: "Resource" },
                { key: "service_code", label: "Service" },
                { key: "allocated_cost", label: "Cost", render: (row) => formatCost(row.allocated_cost, row.currency) },
              ]}
            />
          </article>

          <article className="analytics-panel wide">
            <div className="panel-title"><div><h2>API health</h2><p>Human request volume, failures and response time; service accounts excluded</p></div></div>
            <Table
              rows={data.apiHealth}
              empty="API telemetry will appear after portal activity."
              columns={[
                { key: "api_endpoint", label: "Endpoint", render: (row) => <code>{row.api_method} {row.api_endpoint}</code> },
                { key: "request_count", label: "Requests", render: (row) => formatNumber(row.request_count) },
                { key: "error_count", label: "Errors", render: (row) => formatNumber(row.error_count) },
                { key: "average_duration_ms", label: "Avg", render: (row) => `${formatNumber(row.average_duration_ms, 1)} ms` },
                { key: "p95_duration_ms", label: "P95", render: (row) => `${formatNumber(row.p95_duration_ms, 1)} ms` },
              ]}
            />
          </article>

          <article className="analytics-panel wide">
            <div className="panel-title"><div><h2>CloudWatch utilization</h2><p>Latest server, database and application metrics</p></div></div>
            <Table
              rows={data.cloudWatchMetrics}
              empty="No CloudWatch metrics yet. Connect the metric exporter to populate utilization."
              columns={[
                { key: "resource_name", label: "Resource" },
                { key: "metric_name", label: "Metric" },
                { key: "average_value", label: "Average", render: (row) => `${formatNumber(row.average_value, 2)} ${row.unit || ""}` },
                { key: "maximum_value", label: "Maximum", render: (row) => `${formatNumber(row.maximum_value, 2)} ${row.unit || ""}` },
                { key: "last_metric_at", label: "Last received", render: (row) => row.last_metric_at ? new Date(row.last_metric_at).toLocaleString() : "—" },
              ]}
            />
          </article>
        </div>
      </> : null}
    </section>
  );
}

